import type { HazardsConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import { aabb, copyAabb, type Aabb } from '../physics/Aabb';
import { CraneBeamModel, CraneBlockModel, CraneSwingModel } from './CraneLoadModels';
import { ExcavatorModel } from './ExcavatorModel';
import { HazardKind, type HazardDefinition } from './HazardDefinition';
import type { HazardCue, HazardStepContext } from './HazardModel';
import { VehicleModel } from './VehicleModel';

export type AnyHazardModel = CraneBeamModel | CraneBlockModel | CraneSwingModel | ExcavatorModel | VehicleModel;

export function createHazardModel(definition: HazardDefinition, config: HazardsConfig, world: WorldConfig): AnyHazardModel {
  switch (definition.kind) {
    case HazardKind.CraneBeam:
      return new CraneBeamModel(definition, config.craneBeam);
    case HazardKind.CraneBlock:
      return new CraneBlockModel(definition, config.craneBlock, world);
    case HazardKind.CraneSwing:
      return new CraneSwingModel(definition, config.craneSwing, world);
    case HazardKind.Excavator:
      return new ExcavatorModel(definition, config.excavator, world);
    case HazardKind.Vehicle:
      return new VehicleModel(definition, config, world);
  }
}

/**
 * A spawned moving hazard (gameplay state only): its model plus the colliders in track space –
 * current and from the step before, so the collision system can tell who moved into whom.
 */
export class HazardInstance {
  /** Its socket was armed: the timeline may start. */
  armed = false;
  /**
   * The runner got past it with protection (or lost a life on it): it no longer collides with
   * the runner. The presenter decides what disappears (vehicle, block, heap) and what stays.
   */
  spared = false;
  /** Collider slots in track space (x lateral, y height, z = track distance). */
  readonly trackColliders: Aabb[];
  readonly previousColliders: Aabb[];
  readonly enabled: boolean[];
  readonly previousEnabled: boolean[];
  /** Union of the enabled colliders – broad phase. */
  readonly bounds: Aabb = aabb();

  constructor(
    readonly uid: number,
    readonly definition: HazardDefinition,
    readonly segment: SegmentInstance,
    readonly model: AnyHazardModel,
  ) {
    const count = model.colliders.length;
    this.trackColliders = Array.from({ length: count }, () => aabb());
    this.previousColliders = Array.from({ length: count }, () => aabb());
    this.enabled = Array.from({ length: count }, () => false);
    this.previousEnabled = Array.from({ length: count }, () => false);
    this.sync();
    this.keepPrevious();
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    this.keepPrevious();
    this.model.step(dt, ctx, cues);
    this.sync();
  }

  private keepPrevious(): void {
    for (let i = 0; i < this.trackColliders.length; i++) {
      copyAabb(this.trackColliders[i]!, this.previousColliders[i]!);
      this.previousEnabled[i] = this.enabled[i]!;
    }
  }

  /** Segment-local collider slots → track space; bounds over the enabled ones. */
  private sync(): void {
    const offset = this.segment.startDistance;
    const b = this.bounds;
    b.minX = b.minY = b.minZ = Infinity;
    b.maxX = b.maxY = b.maxZ = -Infinity;
    this.model.colliders.forEach((slot, i) => {
      const box = copyAabb(slot.box, this.trackColliders[i]!);
      box.minZ += offset;
      box.maxZ += offset;
      this.enabled[i] = slot.enabled;
      if (!slot.enabled) return;
      b.minX = Math.min(b.minX, box.minX);
      b.minY = Math.min(b.minY, box.minY);
      b.minZ = Math.min(b.minZ, box.minZ);
      b.maxX = Math.max(b.maxX, box.maxX);
      b.maxY = Math.max(b.maxY, box.maxY);
      b.maxZ = Math.max(b.maxZ, box.maxZ);
    });
  }
}
